// src/views/menage/ReprisesAgent.jsx — LES CARTES DE CONTRÔLE, EN TÊTE DE LA
// JOURNÉE DE L'AGENT CONTRÔLÉ (recalage maquette Dimitri 2026-08-19).
//
// CHAQUE CONTRÔLE EST UNE CARTE FERMÉE — bordure, rayon 12, en-tête (pastille
// carrée 32 px + nom du logement 12.5 gras), intertitre 10 px majuscules.
// TROIS NATURES, TROIS COULEURS — distinguables AVANT de lire :
//   · TERRACOTTA « À REPRENDRE » (reprise_par 'agent', en cours) : son
//     travail — cases 19 px bordées ciel, un point fait passe VERT dans la
//     liste, compteur « 1/3 » dans l'en-tête, bouton « Terminer · 1 sur 3 »
//     inactif tant que tout n'est pas coché ;
//   · AMBRE « POUR INFORMATION » (repris sur place par le contrôleur) : il
//     apprend — puces simples, AUCUNE case, AUCUNE coche ;
//   · VERT « CE QUE J'AI REPRIS » (reprise_par 'agent', réglé) : terminé —
//     coches pleines, ses photos de reprise en vignettes 32 px.
// ORDRE : ce qui attend (terracotta), puis l'information (ambre), puis le
// fini (vert). Plusieurs cartes coexistent, chacune fermée sur elle-même.
//
// 🔴 AUCUNE IDENTITÉ DE CONTRÔLEUR : ni affichée, NI REÇUE — le serveur ne
// sélectionne jamais controleur_email (anonymat structurel). Ne jamais
// afficher « contrôlé par … » ici, la donnée n'existe pas côté client.
// Heures françaises (« 16h »), heure d'arrivée = THÉORIQUE (jamais le réel).
//
// MAQUETTE NEST v677 (journée du 28/10) : dans « Mon planning », une reprise à
// faire est une carte de la journée, « À reprendre · <logement> ». Ce composant
// lit les reprises, dit à la journée lesquelles attendent (`onCharge`) et ouvre
// la feuille de celle qu'on lui passe dans `ouverte`. Sans `ouverte` (undefined),
// il rend la liste. Le nom du logement vient de la porte de la journée (`noms`),
// jamais de la configuration. Vert et aubergine seulement : aucun bleu.

import { useEffect, useState } from 'react'
import { Info, AlertTriangle, Check, Camera } from 'lucide-react'
import { DS } from '../../lib/ui'
import { fetchReprisesAgent, pointFait, photoReprise } from '../../lib/controle-agent-api'
import { fichierEnBase64 } from '../../lib/controle-api'
import { compressImage } from '../../lib/image-compression'
import { FeuilleAgent } from './CartePlanning'
import { NEST } from './palette-nest'

// Les trois natures (maquette 2026-08-19) — la couleur se lit avant les mots.
const NATURES = {
  terracotta: {
    bord: NEST.aubBord, entete: NEST.aubPale, pastille: NEST.aub, texte: NEST.aubTexte,
    intertitre: 'À REPRENDRE', icone: AlertTriangle, corps: '#fff',
  },
  ambre: {
    bord: NEST.bord, entete: NEST.fondCarte, pastille: NEST.grisFonce, texte: NEST.grisTexte,
    intertitre: 'POUR INFORMATION', icone: Info, corps: '#fdfbf6',
  },
  vert: {
    bord: NEST.vertBord, entete: NEST.vertLigne, pastille: NEST.vert, texte: NEST.vertFonce,
    intertitre: "CE QUE J'AI REPRIS", icone: Check, corps: '#f7fbf9',
  },
}

// 'HH:MM' → « 16h » / « 16h30 » (heures françaises, jamais « 16:00 »).
export function heureFrancaise(hhmm) {
  const m = String(hhmm || '').match(/^(\d{2}):(\d{2})$/)
  if (!m) return null
  const h = String(Number(m[1]))
  return m[2] === '00' ? `${h}h` : `${h}h${m[2]}`
}

// ISO → « 14h05 » (Europe/Paris) — l'heure du « terminé à ».
export function heureDeIso(iso) {
  if (!iso) return null
  try {
    const hm = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso)).replace(':', 'h')
    return hm.startsWith('0') ? hm.slice(1) : hm
  } catch (_) { return null }
}

function todayIsoParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
}

// échéance → « à faire avant 16h » (aujourd'hui) / « à faire avant le 22/08,
// 16h » (plus tard) / null sans arrivée connue → la mention est OMISE.
export function libelleEcheance(echeance, todayIso = todayIsoParis()) {
  const heure = heureFrancaise(echeance?.heure)
  if (!heure) return null
  if (echeance.date === todayIso) return `à faire avant ${heure}`
  const [, mm, dd] = String(echeance.date || '').split('-')
  return dd ? `à faire avant le ${dd}/${mm}, ${heure}` : `à faire avant ${heure}`
}

// « salle_de_bain » → « Salle de bain » — réplique locale volontaire de
// humaniserId (ControleView) : pas d'import croisé entre les deux fronts.
function humaniser(id) {
  const s = String(id || '').replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}

// logement_id → nom lisible — repli neutre « votre logement », jamais l'id.
function libelleLogement(noms, logementId) {
  return (noms && noms[logementId]) || 'votre logement'
}

// La nature d'un contrôle — et l'ORDRE de la journée (attend → info → fini).
export function natureDe(controle) {
  if (controle.reprise_par === 'controleur') return 'ambre'
  if (controle.statut === 'regle' || controle.termineIci) return 'vert'
  return 'terracotta'
}
const ORDRE = { terracotta: 0, ambre: 1, vert: 2 }

// ── L'EN-TÊTE d'une carte : pastille 32 px + nom + sous-titre (+ compteur). ─
function EnTete({ nature, logement, sousTitre, compteur, testId }) {
  const N = NATURES[nature]
  const Icone = N.icone
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
      background: N.entete, padding: '10px 12px' }}>
      <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 9,
        background: N.pastille, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0 }}>
        <Icone size={17} strokeWidth={2.4} color="#fff" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700,
          color: N.texte, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }}>{logement}</span>
        <span data-testid={`${testId}-sous-titre`}
          style={{ display: 'block', fontSize: 11, color: N.texte, opacity: .92 }}>
          {sousTitre}
        </span>
      </span>
      {compteur && (
        <span data-testid={`${testId}-compteur`}
          style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 700,
            fontVariantNumeric: 'tabular-nums', color: NATURES.terracotta.texte,
            background: '#fff', border: `1px solid ${NEST.aubBord}`, borderRadius: 9,
            padding: '3px 8px' }}>
          {compteur}
        </span>
      )}
    </div>
  )
}

function Intertitre({ nature }) {
  const N = NATURES[nature]
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.09em',
      textTransform: 'uppercase', color: N.texte, opacity: .85,
      padding: '8px 12px 0' }}>
      {N.intertitre}
    </div>
  )
}

// Les photos du CONSTAT (points du contrôleur) — 56 px, lisibles.
function PhotosConstat({ point, bord }) {
  if (!point.photos?.length) return null
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
      {point.photos.map((url, i) => (
        <img key={i} src={url} alt={`Constat — ${humaniser(point.categorie)}`}
          style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8,
            border: `0.5px solid ${bord}55` }} />
      ))}
    </div>
  )
}

// ── LA CARTE ────────────────────────────────────────────────────────────────
function CarteControle({ controle, noms, feuille = false, onPointFait, onTerminer, onPhotoReprise }) {
  const nature = natureDe(controle)
  const N = NATURES[nature]
  const nb = controle.points.length
  const s = nb > 1 ? 's' : ''
  const logement = libelleLogement(noms, controle.logement_id)
  const faits = controle.faits.filter(Boolean).length
  const tousFaits = nb > 0 && faits >= nb

  const sousTitre = nature === 'ambre'
    ? `${nb} point${s} signalé${s} · déjà repris sur place`
    : nature === 'vert'
      ? `${nb} point${s} repris${heureDeIso(controle.repris_le) ? ` · terminé à ${heureDeIso(controle.repris_le)}` : ''}`
      : `${nb} point${s}${libelleEcheance(controle.echeance) ? ` · ${libelleEcheance(controle.echeance)}` : ''}`

  return (
    <div data-testid={`carte-controle-${controle.id}`} data-nature={nature}
      style={{ border: `1px solid ${N.bord}`, borderRadius: 12, overflow: 'hidden',
        marginBottom: 10, background: N.corps, fontFamily: DS.fontBody }}>
      {/* Dans la feuille, son titre nomme déjà le logement (maquette). */}
      {feuille ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 0', fontSize: 12, color: NEST.grisFonce }}>
          <span data-testid={`carte-controle-${controle.id}-sous-titre`} style={{ flex: 1 }}>{sousTitre}</span>
          {nature === 'terracotta' && <b data-testid={`carte-controle-${controle.id}-compteur`}>{faits}/{nb}</b>}
        </div>
      ) : (<>
        <EnTete nature={nature} logement={logement} sousTitre={sousTitre}
          compteur={nature === 'terracotta' ? `${faits}/${nb}` : null}
          testId={`carte-controle-${controle.id}`} />
        <Intertitre nature={nature} />
      </>)}

      <div style={{ padding: '4px 12px 12px' }}>
        {controle.erreur && (
          <div style={{ fontSize: 12, color: NATURES.terracotta.texte, padding: '6px 0' }}>
            {controle.erreur}
          </div>
        )}

        {/* AMBRE — puces simples, aucune case, aucune coche. */}
        {nature === 'ambre' && controle.points.map((pt, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, padding: '7px 0',
            borderTop: i > 0 ? `0.5px solid ${N.bord}55` : 'none' }}>
            <span aria-hidden="true" style={{ flexShrink: 0, width: 7, height: 7,
              borderRadius: '50%', background: N.pastille, marginTop: 5 }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: N.texte }}>
              <strong>{humaniser(pt.categorie)}</strong> — {pt.texte}
              <PhotosConstat point={pt} bord={N.bord} />
            </span>
          </div>
        ))}

        {/* TERRACOTTA — cases 19 px bordées ciel ; un point FAIT passe VERT
            dans la liste (bordure #c9e0d4, coche pleine) pendant que les
            autres attendent. */}
        {nature === 'terracotta' && (
          <>
            {controle.points.map((pt, i) => {
              const fait = !!controle.faits[i]
              return (
                <div key={i} data-testid={`point-reprise-${controle.id}-${i}`}
                  data-fait={fait ? 'oui' : 'non'}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '8px 9px', marginTop: 6, borderRadius: 9,
                    border: `1px solid ${fait ? NATURES.vert.bord : `${N.bord}88`}`,
                    background: fait ? NATURES.vert.corps : '#fff' }}>
                  {/* La CIBLE reste tactile (44 px), la case DESSINÉE fait 19 px. */}
                  <button type="button" role="checkbox" aria-checked={fait}
                    data-testid={`case-point-${controle.id}-${i}`}
                    aria-label={`Point repris : ${humaniser(pt.categorie)}`}
                    disabled={fait}
                    onClick={() => onPointFait(controle.id, i)}
                    className="fbz-press fbz-tap"
                    style={{ flexShrink: 0, width: 44, height: 44, margin: -10,
                      padding: 0, background: 'none', border: 'none',
                      display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center',
                      cursor: fait ? 'default' : 'pointer' }}>
                    <span aria-hidden="true" style={{ width: 19, height: 19,
                      borderRadius: 5, boxSizing: 'border-box',
                      border: `2px solid ${fait ? NATURES.vert.pastille : NEST.boutonBord}`,
                      background: fait ? NATURES.vert.pastille : '#fff',
                      display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center' }}>
                      {fait && <Check size={13} strokeWidth={3} color="#fff" />}
                    </span>
                  </button>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45,
                    color: fait ? NATURES.vert.texte : N.texte }}>
                    <strong>{humaniser(pt.categorie)}</strong> — {pt.texte}
                    <PhotosConstat point={pt} bord={N.bord} />
                    {controle.photoErreurs?.[i] && (
                      <span style={{ display: 'block', fontSize: 11,
                        color: NATURES.terracotta.texte, marginTop: 4 }}>
                        {controle.photoErreurs[i]}
                      </span>
                    )}
                    {/* PHOTO DE LA REPRISE (Dimitri 2026-08-19) — FACULTATIVE,
                        une seule par point ; le bouton disparaît une fois la
                        photo prise. INDÉPENDANTE de la coche : un envoi en
                        cours n'empêche jamais de cocher. */}
                    {!controle.reprise?.[i]?.photo && (
                      <label data-testid={`photo-reprise-bouton-${controle.id}-${i}`}
                        className="fbz-press fbz-tap"
                        style={{ display: 'flex', alignItems: 'center',
                          justifyContent: 'center', gap: 7, minHeight: 44,
                          marginTop: 7, borderRadius: 9,
                          border: `1.5px dashed ${NEST.boutonBord}`, background: NEST.fondCarte,
                          fontSize: 11.5, fontWeight: 700, color: NEST.boutonTexte,
                          cursor: controle.photosEnvoi?.[i] ? 'default' : 'pointer',
                          opacity: controle.photosEnvoi?.[i] ? .65 : 1 }}>
                        <Camera size={15} strokeWidth={2.1} color={NEST.boutonTexte} />
                        {controle.photosEnvoi?.[i] ? 'Envoi de la photo…' : 'Photo de la reprise'}
                        <input type="file" accept="image/*" capture="environment"
                          data-testid={`photo-reprise-input-${controle.id}-${i}`}
                          style={{ display: 'none' }}
                          disabled={!!controle.photosEnvoi?.[i]}
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0]
                            e.target.value = ''
                            if (f) onPhotoReprise(controle.id, i, f)
                          }} />
                      </label>
                    )}
                  </span>
                  {controle.reprise?.[i]?.photo && (
                    <img data-testid={`photo-reprise-${controle.id}-${i}`}
                      src={controle.reprise[i].photo}
                      alt={`Reprise — ${humaniser(pt.categorie)}`}
                      style={{ flexShrink: 0, width: 32, height: 32, objectFit: 'cover',
                        borderRadius: 7, border: `0.5px solid ${NATURES.vert.bord}` }} />
                  )}
                </div>
              )
            })}
            <button type="button" data-testid={`terminer-${controle.id}`}
              disabled={!tousFaits}
              onClick={() => tousFaits && onTerminer(controle.id)}
              className="fbz-press fbz-tap"
              style={{ width: '100%', minHeight: 44, marginTop: 10,
                background: tousFaits ? NATURES.vert.pastille : '#f2f4f6',
                color: tousFaits ? '#fff' : '#98a2ad',
                border: tousFaits ? 'none' : '1px solid #e3e8ee',
                borderRadius: 10, fontSize: 12.5, fontWeight: 700,
                fontFamily: DS.fontBody,
                cursor: tousFaits ? 'pointer' : 'default' }}>
              Terminer · {faits} sur {nb}
            </button>
          </>
        )}

        {/* VERT — coches pleines, SES photos de reprise en vignettes 32 px. */}
        {nature === 'vert' && controle.points.map((pt, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9,
            padding: '7px 0', borderTop: i > 0 ? `0.5px solid ${N.bord}88` : 'none' }}>
            <Check size={15} strokeWidth={2.8} color={N.texte}
              style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: N.texte }}>
              <strong>{humaniser(pt.categorie)}</strong> — {pt.texte}
            </span>
            {controle.reprise?.[i]?.photo && (
              <img data-testid={`photo-reprise-${controle.id}-${i}`}
                src={controle.reprise[i].photo}
                alt={`Reprise — ${humaniser(pt.categorie)}`}
                style={{ flexShrink: 0, width: 32, height: 32, objectFit: 'cover',
                  borderRadius: 7, border: `0.5px solid ${N.bord}` }} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Ce qui attend, tel que la journée l'affiche (« avant 16h »).
function resumeAttente(controles, noms) {
  const attente = controles.filter((c) => c.reprise_par !== 'controleur' && c.statut !== 'regle' && !c.termineIci)
  const liste = attente.map((c) => ({
    id: c.id, run_id: c.run_id || null, logement_id: c.logement_id, scheduled_date: c.scheduled_date,
    logement: libelleLogement(noms, c.logement_id),
    echeance: heureFrancaise(c.echeance?.heure) ? `avant ${heureFrancaise(c.echeance.heure)}` : "aujourd'hui",
  }))
  return { erreur: false, attente: liste.length, logement: liste[0]?.logement || null, liste }
}

export default function ReprisesAgent({ currentUser, noms = {}, onCharge = null, ouverte, onFermer = null }) {
  const [controles, setControles] = useState(null)

  const role = currentUser?.role
  const concerne = role === 'menage' || role === 'orchestrateur'

  useEffect(() => {
    if (!concerne || !currentUser?.email) return
    let annule = false
    ;(async () => {
      try {
        const payload = await fetchReprisesAgent()
        if (annule) return
        const liste = (payload?.reprises || [])
        setControles(liste.map((r) => ({
          ...r,
          points: Array.isArray(r.points) ? r.points : [],
          faits: (Array.isArray(r.reprise) ? r.reprise : []).map((x) => !!x?.fait),
          erreur: null,
          termineIci: false,
        })))
      } catch (e) {
        // Jamais bloquant : la journée vit sans carte, et sa tuile dit « — ».
        console.error('[reprises-agent] chargement', e?.message || e)
        if (!annule && onCharge) onCharge({ erreur: true, attente: 0, logement: null, liste: [] })
      }
    })()
    return () => { annule = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concerne, currentUser?.email])

  // La journée suit : une reprise terminée quitte ses cartes et sa tuile.
  useEffect(() => {
    if (onCharge && controles) onCharge(resumeAttente(controles, noms))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controles, noms])

  if (!concerne || !controles || controles.length === 0) return null

  // Coche OPTIMISTE : la case se remplit tout de suite ; si le serveur refuse,
  // ROLLBACK + message court — jamais une coche fantôme. La carte reste
  // TERRACOTTA même tout coché : c'est « Terminer » qui la fait passer verte
  // (le serveur, lui, a déjà clos au dernier point — repris_le rendu).
  const cocher = async (controleId, index) => {
    setControles((prev) => prev.map((c) => (c.id === controleId
      ? { ...c, faits: c.faits.map((f, i) => (i === index ? true : f)), erreur: null }
      : c)))
    try {
      const rep = await pointFait({ controleId, index })
      if (rep?.statut) {
        setControles((prev) => prev.map((c) => (c.id === controleId
          ? { ...c,
              // ⚠️ PAS le statut serveur en direct : « Terminer » garde la main
              // sur la bascule visuelle — on mémorise repris_le pour l'en-tête.
              repris_le: rep.repris_le || c.repris_le || (rep.statut === 'regle' ? new Date().toISOString() : null),
              faits: (rep.reprise || c.faits.map((f, i) => ({ index: i, fait: f }))).map((x) => !!x?.fait) }
          : c)))
      }
    } catch (e) {
      setControles((prev) => prev.map((c) => (c.id === controleId
        ? { ...c, faits: c.faits.map((f, i) => (i === index ? false : f)),
            erreur: e?.message || 'Enregistrement impossible. Réessayez.' }
        : c)))
    }
  }

  // La PHOTO DE LA REPRISE — compression puis serveur (base64) ; succès →
  // vignette immédiate (photo_url signée), le bouton disparaît ; échec →
  // message court sous le point, le bouton reste. INDÉPENDANTE de la coche :
  // aucun verrou partagé, chaque point porte son propre état d'envoi.
  const prendrePhoto = async (controleId, index, file) => {
    setControles((prev) => prev.map((c) => (c.id === controleId
      ? { ...c, photosEnvoi: { ...(c.photosEnvoi || {}), [index]: true },
          photoErreurs: { ...(c.photoErreurs || {}), [index]: null } }
      : c)))
    try {
      let blob = file
      let mime = file.type || 'image/jpeg'
      try {
        const compresse = await compressImage(file, { targetBytes: 900 * 1024 })
        if (compresse) { blob = compresse.blob || compresse; mime = (compresse.blob || compresse).type || mime }
      } catch (e) { console.error('[reprises-agent] compression', e?.message || e) }
      const dataBase64 = await fichierEnBase64(blob)
      const json = await photoReprise({ controleId, index, mime, dataBase64 })
      setControles((prev) => prev.map((c) => (c.id === controleId
        ? { ...c,
            photosEnvoi: { ...(c.photosEnvoi || {}), [index]: false },
            reprise: (c.reprise || []).map((x, i) => (i === index
              ? { ...x, photo: json?.photo_url || x.photo } : x)) }
        : c)))
    } catch (e) {
      setControles((prev) => prev.map((c) => (c.id === controleId
        ? { ...c, photosEnvoi: { ...(c.photosEnvoi || {}), [index]: false },
            photoErreurs: { ...(c.photoErreurs || {}), [index]: e?.message || 'Envoi impossible. Réessayez.' } }
        : c)))
    }
  }

  const terminer = (controleId) => {
    setControles((prev) => prev.map((c) => (c.id === controleId
      ? { ...c, termineIci: true } : c)))
  }

  // L'ORDRE de la journée : ce qui ATTEND d'abord, puis l'information, puis
  // le fini — stable à l'intérieur d'une nature (ordre serveur).
  const tries = [...controles].sort((a, b) => ORDRE[natureDe(a)] - ORDRE[natureDe(b)])

  if (ouverte !== undefined) {
    const c = controles.find((x) => x.id === ouverte)
    if (!c) return null
    return (
      <FeuilleAgent titre={`À reprendre · ${libelleLogement(noms, c.logement_id)}`} testId={`feuille-reprise-${c.id}`}
        onFermer={onFermer}>
        <CarteControle controle={c} noms={noms} feuille
          onPointFait={cocher} onTerminer={terminer} onPhotoReprise={prendrePhoto} />
      </FeuilleAgent>
    )
  }

  return (
    <div data-testid="reprises-agent" style={{ marginBottom: 14 }}>
      {tries.map((c) => (
        <CarteControle key={c.id} controle={c} noms={noms}
          onPointFait={cocher} onTerminer={terminer} onPhotoReprise={prendrePhoto} />
      ))}
    </div>
  )
}
